/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Heading, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { reaction } from 'mobx';
import * as React from 'react';
import { getSubscriptionValues } from '../../Utilities/subscription-manager';
import LinksComponent from './components/links';
import { ISubscriptionRestrictionButtonData } from './subscription-restriction-button.data';
import { ISubscriptionRestrictionButtonProps } from './subscription-restriction-button.props.autogenerated';
export interface ISubscriptionRestrictionButtonViewProps extends ISubscriptionRestrictionButtonProps<{}> {
    title?: React.ReactNode;
    text?: React.ReactNode;
    image?: React.ReactNode;
    links?: React.ReactNode;
    className?: string;
    hasFrequency: boolean;
    hasName: boolean;
    contentBlockContainer: IModuleProps;
    imageContainer: INodeProps;
    detailsContainer: INodeProps;
}

/**
 *
 * ContentCard component
 * @extends {React.PureComponent<ISubscriptionRestrictionButtonProps{}>}
 */

class SubscriptionRestrictionButton extends React.PureComponent<ISubscriptionRestrictionButtonProps<ISubscriptionRestrictionButtonData>> {

    public constructor(props: ISubscriptionRestrictionButtonProps<ISubscriptionRestrictionButtonData>) {
        super(props);

        reaction(
            () => { return [this.props.data.cart, this.props.data.cart?.result, this.props.data.cart?.result?.cart, this.props.data.cart?.result?.cart.ExtensionProperties]; },
            () => {
                this.forceUpdate();
            }
        );
    }

    public render(): JSX.Element {
        const values = getSubscriptionValues(this.props.data.cart?.result?.cart);

        const { heading, paragraph, image, links, className } = this.props.config;
        const contentBlockTitle = heading && heading.text && <Heading className='ms-content-block__title' headingTag={heading.tag} text={heading.text} />;
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings || {},
            imageSettings: image && image.imageSettings
        };
        const contentBlockLinks = links && links.length > 0 && <LinksComponent {...{ links }} />;
        const contentBlockText = paragraph && <Msdyn365.RichTextComponent text={paragraph} className='ms-content-block__text' />;
        const contentBlockImage = image && <Msdyn365.Image {...image} {...imageProps} />;

        if (!contentBlockTitle && !contentBlockText && !contentBlockImage && !contentBlockLinks) {
            return <div />;
        }

        const subscriptionRestrictionButtonViewProps = {
            ...this.props,
            title: contentBlockTitle,
            text: contentBlockText,
            image: contentBlockImage,
            links: contentBlockLinks,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('ms-content-block', className)
            },
            className: className,
            imageContainer: { className: 'ms-content-block__image' },
            detailsContainer: { className: 'ms-content-block__details' },
            hasFrequency: !!values.frequency,
            hasName: !!values.name
        };

        return this.props.renderView(subscriptionRestrictionButtonViewProps) as React.ReactElement;
    }
}

export default SubscriptionRestrictionButton;
